import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageMetadata } from '../hooks/usePageMetadata';
import {
    Building2,
    TrendingUp,
    Shield,
    Globe,
    DollarSign,
    Users,
    ArrowRight,
    CheckCircle,
    Eye,
    MessageCircle,
    Home,
    Building,
    PieChart,
    Loader2,
    AlertCircle,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import ProjectCard from '../components/projects/ProjectCard';
import { useGetPublicProjectsQuery } from '../store/slices/apiSlice';

const Investment = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

    // Set page metadata
    usePageMetadata('investment');

    // Fetch projects from API
    const { data: projectsResponse, isLoading: loading, error: projectsError } = useGetPublicProjectsQuery();
    const projects = Array.isArray(projectsResponse?.data) ? projectsResponse?.data :
        Array.isArray(projectsResponse?.data?.projects) ? projectsResponse?.data?.projects : [];
    const error = projectsError ? 'Failed to load projects. Please try again later.' : null;

    useEffect(() => {
        setIsVisible(true);
    }, []);

    // Investment Types
    const investmentTypes = [
        {
            title: "Real Estate Investment Nigeria",
            description: "Invest in premium Nigerian real estate properties with high growth potential and stable returns.",
            icon: Home,
            path: "/investment/real-estate-investment-nigeria",
            color: "from-blue-500 to-blue-600",
            features: ["Prime Locations", "High ROI", "Stable Returns", "Expert Management"],
            stats: { roi: "15-25%", duration: "3-5 years", minInvestment: "$5,000" }
        },
        {
            title: "Real Estate Investing from Abroad",
            description: "Seamlessly invest in Nigerian real estate from anywhere in the world with our global platform.",
            icon: Globe,
            path: "/investment/real-estate-investing-from-abroad",
            color: "from-green-500 to-green-600",
            features: ["Global Access", "Remote Management", "Currency Options", "Legal Support"],
            stats: { roi: "12-20%", duration: "2-4 years", minInvestment: "$3,000" }
        },
        {
            title: "Commercial Property Investment Nigeria",
            description: "Invest in commercial properties including offices, retail spaces, and industrial facilities.",
            icon: Building,
            path: "/investment/commercial-property-investment-nigeria",
            color: "from-purple-500 to-purple-600",
            features: ["High Rental Yield", "Long-term Leases", "Prime Locations", "Professional Tenants"],
            stats: { roi: "18-30%", duration: "5-10 years", minInvestment: "$10,000" }
        },
        {
            title: "Residential Property Investment Nigeria",
            description: "Invest in residential properties including apartments, houses, and luxury developments.",
            icon: Building2,
            path: "/investment/residential-property-investment-nigeria",
            color: "from-orange-500 to-orange-600",
            features: ["Growing Demand", "Stable Tenants", "Appreciation", "Regular Income"],
            stats: { roi: "10-18%", duration: "3-7 years", minInvestment: "$5,000" }
        },
        {
            title: "Fractional Property Investment Nigeria",
            description: "Own a fraction of premium properties with lower investment requirements and shared ownership benefits.",
            icon: PieChart,
            path: "/investment/fractional-property-investment-nigeria",
            color: "from-pink-500 to-pink-600",
            features: ["Low Entry Cost", "Diversified Portfolio", "Professional Management", "Liquidity Options"],
            stats: { roi: "8-15%", duration: "1-3 years", minInvestment: "$500" }
        }
    ];

    // Why Choose Us
    const whyChooseUs = [
        {
            icon: Shield,
            title: "Secure & Regulated",
            description: "All investments are backed by proper legal documentation and regulatory compliance."
        },
        {
            icon: TrendingUp,
            title: "Proven Track Record",
            description: "Consistent returns with over 200 successful projects and satisfied investors."
        },
        {
            icon: Users,
            title: "Expert Team",
            description: "Professional team with deep knowledge of Nigerian real estate market."
        },
        {
            icon: Globe,
            title: "Global Platform",
            description: "Invest from anywhere in the world with our user-friendly platform."
        }
    ];

    // Helper function to format currency
    const formatCurrency = (amount: number, currencySymbol: string = '$') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        }).format(amount).replace('$', currencySymbol);
    };

    // Calculate real stats from projects
    const calculateStats = () => {
        const totalFundingGoal = projects.reduce((sum, project) => sum + project.funding_goal, 0);
        const totalRaised = projects.reduce((sum, project) => sum + (project.amount_raised || 0), 0);
        const averageROI = projects.length > 0
            ? projects.reduce((sum, project) => sum + parseFloat(project.expected_roi_percentage), 0) / projects.length
            : 0;

        return {
            totalInvestment: formatCurrency(totalFundingGoal),
            totalRaised: formatCurrency(totalRaised),
            projectCount: projects.length,
            averageROI: `${averageROI.toFixed(1)}%`
        };
    };

    const stats = calculateStats();

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            {/* Hero Section */}
            <section className="relative pt-20 pb-16 bg-white">
                <div className="container mx-auto px-6 relative z-10">
                    <div className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <TrendingUp className="w-4 h-4" />
                            Investment Platform
                        </div>

                        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                            Your Gateway to
                            <span className="text-blue-600"> Nigerian Investments</span>
                        </h1>

                        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                            Explore diverse investment opportunities across Nigeria's thriving real estate market.
                            From residential properties to commercial developments, find the perfect investment that matches your goals and budget.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/investment/real-estate-investment-nigeria"
                                className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 hover:-translate-y-1"
                            >
                                Explore Opportunities
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                            <Link
                                to="/projects"
                                className="inline-flex items-center gap-2 bg-white text-gray-700 px-8 py-4 rounded-xl font-semibold border border-gray-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                            >
                                <Eye className="w-5 h-5" />
                                View Projects
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Investment Types Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Investment Opportunities
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Choose from our diverse range of investment options tailored to different risk profiles and investment goals.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {investmentTypes.map((investment, index) => (
                            <div
                                key={index}
                                className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className={`h-2 bg-blue-600`}></div>

                                <div className="p-8">
                                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                        <investment.icon className="w-8 h-8 text-white" />
                                    </div>

                                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                                        {investment.title}
                                    </h3>

                                    <p className="text-gray-600 mb-6 leading-relaxed">
                                        {investment.description}
                                    </p>

                                    <div className="space-y-3 mb-6">
                                        {investment.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-center gap-2">
                                                <CheckCircle className="w-4 h-4 text-green-500" />
                                                <span className="text-sm text-gray-600">{feature}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                                        <div className="text-center">
                                            <div className="text-lg font-bold text-gray-900">{investment.stats.roi}</div>
                                            <div className="text-xs text-gray-500">ROI</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-lg font-bold text-gray-900">{investment.stats.duration}</div>
                                            <div className="text-xs text-gray-500">Duration</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-lg font-bold text-gray-900">{investment.stats.minInvestment}</div>
                                            <div className="text-xs text-gray-500">Min. Investment</div>
                                        </div>
                                    </div>

                                    <Link
                                        to={investment.path}
                                        className={`block w-full text-center py-3 bg-blue-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
                                    >
                                        Learn More
                                        <ArrowRight className="w-4 h-4 inline ml-2" />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Projects Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Featured Investment Opportunities
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Discover our most promising investment projects with high returns and strong potential.
                        </p>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="flex items-center gap-3">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                                <span className="text-lg text-gray-600">Loading projects...</span>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="text-center">
                                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Projects</h3>
                                <p className="text-gray-600 mb-4">{error}</p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-300"
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {projects.slice(0, 6).map((project, index) => (
                                <ProjectCard
                                    key={project.project_id}
                                    project={project}
                                    className={`transition-all duration-1000 delay-${index * 100} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                />
                            ))}
                        </div>
                    )}

                    {!loading && !error && projects.length > 6 && (
                        <div className="text-center mt-12">
                            <Link
                                to="/projects"
                                className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 hover:-translate-y-1"
                            >
                                View All Projects
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>
                    )}
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Why Choose DiasporaWise?
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            We provide the tools, expertise, and support you need to make successful real estate investments in Nigeria.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {whyChooseUs.map((item, index) => (
                            <div
                                key={index}
                                className={`text-center transition-all duration-1000 delay-${index * 100} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                            >
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-3xl mb-6 group">
                                    <item.icon className="w-10 h-10 text-blue-600 group-hover:scale-110 transition-transform duration-300" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-blue-600">
                <div className="container mx-auto px-6 text-center">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Ready to Start Your Investment Journey?
                        </h2>
                        <p className="text-xl text-blue-100 mb-8">
                            Join thousands of successful investors who have built wealth through Nigerian real estate.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/investment/real-estate-investment-nigeria"
                                className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                            >
                                Get Started Today
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 bg-transparent text-white px-8 py-4 rounded-xl font-semibold border-2 border-white hover:bg-white hover:text-blue-600 transition-all duration-300 hover:-translate-y-1"
                            >
                                <MessageCircle className="w-5 h-5" />
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Learn More About Nigerian Real Estate Investment Section */}
            <section className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-8">
                        <button
                            onClick={() => setIsLearnMoreOpen(!isLearnMoreOpen)}
                            className="flex items-center justify-center gap-2 mx-auto bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300 border border-gray-300"
                            aria-expanded={isLearnMoreOpen}
                            aria-controls="investment-content"
                        >
                            <span className="font-medium">
                                {isLearnMoreOpen ? 'Hide Learn More About Nigerian Real Estate Investment' : 'Learn More About Nigerian Real Estate Investment'}
                            </span>
                            {isLearnMoreOpen ? (
                                <ChevronUp className="w-4 h-4 text-gray-600" />
                            ) : (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                            )}
                        </button>
                    </div>

                    <div
                        id="investment-content"
                        className={`overflow-hidden transition-all duration-500 ease-in-out ${isLearnMoreOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
                            }`}
                    >
                        <article className="prose prose-lg max-w-none">
                            {/* Main Content Section */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                    Introduction: Why Nigerian Real Estate is a Strong Investment Choice
                                </h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Africa's largest economy, Nigeria is also one of the fastest growing real estate markets on the continent. With a population exceeding 220 million and very fast urbanization, the need for housing, commercial facilities and infrastructure is on the rise. This demand brings plenty of opportunities for local and foreign investors who seek steady financial growth in the long run.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    For lots of Nigerians in the diaspora, investing back home has long been a case of sending over money (remitting), to help their families pay for education, healthcare or simply day-to-day living. That may be useful, but it's not how new wealth is created. That's where Nigerian real estate investment is different. By investing in hard assets such as land, apartment blocks, office buildings and construction projects, they can accumulate wealth over the long term while advancing a country's development.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Real estate investment in Nigeria is not just about churning profit, it's also about nation building. Every new suburb, shopping mall or office block contributes to growth, jobs and improved living standards. Now, with platforms like Diasporawise, diaspora Nigerians have structured, safe and transparent investment opportunities in property projects back home.
                                </p>
                            </div>

                            {/* Section 2 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">The Growing Importance of Diaspora Investment in Nigeria</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    The Nigerian diaspora is one of the largest in the world with millions living in US, UK, Europe and Asia. Each year, this community remits billions of dollars to Nigeria. Remittances are a significant part of Nigeria's foreign inflow, often more than oil earnings, according to the World Bank.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    But remittances are a short-term form of financial help. They pay bills and take care of expenses, but they don't necessarily buy assets that grow in value over time. Here's where diaspora real estate investing comes in as a game-changer:
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Asset Building</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Instead of one-off transfers, investment in property builds wealth.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Security</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    People need somewhere to live, and owning a home is one of the most secure assets you can have.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Cultural Connection</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Beyond financial returns, property investment among Nigerians in the diaspora serves as an anchor to their roots — a way of holding onto a piece of home and leaving behind something for future generations.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Economic Development</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Investment in property by the diaspora helps pump money into the economy for housing, construction, and infrastructure.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Today, with digital property investment platforms and secure property management solutions in place, the diaspora investor need not depend on uncertain informal channels or relatives for investment management. They can safely purchase into Nigeria's thriving real estate market, monitor their investments online, and earn secure returns.
                                </p>
                            </div>

                            {/* Section 3 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Reasons to Invest in Nigeria's Real Estate Market</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Strong Housing and Infrastructure Demand</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Nigeria's housing deficit is estimated at 17 million units. As cities such as Lagos, Abuja, and Port Harcourt continue to expand rapidly, the need for residential estates, apartments, and land has never been higher.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Attractive Returns on Investment</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Property investing in Nigeria is increasingly profitable. Investors can earn from: Rental income from residential and commercial units. Property value appreciation as urban expansion drives demand.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Diverse Investment Opportunities</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Whether you're interested in luxury apartments, affordable housing, commercial centers, or land acquisition, real estate investment in Nigeria offers opportunities for every budget.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Government Support</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    The Nigerian government continues to introduce policies and incentives that encourage diaspora property investment, particularly in housing and infrastructure development.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Safe Investment Platforms</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    With solutions like Diasporawise, investors can now explore secure real estate investments in Nigeria with complete transparency, project vetting, and long-term returns.
                                </p>
                            </div>

                            {/* Section 4 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Investment Categories You Can Explore</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    The Nigerian real estate market is broad and welcoming to various types of investors. Major categories include:
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">a) Overview of the Market</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    The property market is divided into residential, commercial, and mixed-use developments. Investors can choose between short-term rental, long-term leasing, or development projects depending on their goals.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">b) Real Estate Investment Nigeria</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Options include: Housing estates. Apartment complexes. Office buildings. Retail centers. Land development projects.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">c) Real Estate Investing from Abroad</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    For diaspora investors, digital real estate platforms allow secure investment without being physically present. Nigerians abroad can: Select vetted property projects online. Use safe digital payment channels. Receive project updates and financial statements.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">d) Commercial Property Investment Nigeria</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Commercial property offers some of the fastest-growing investment opportunities in Nigeria, such as: Shopping malls and retail centers. Office complexes and business hubs. Mixed-use developments.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">e) Residential Property Investment Nigeria</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Nigeria's population boom drives demand for both affordable and luxury housing. Options include: Housing estates. Gated communities. Apartment buildings. Short-let rental apartments.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">f) Fractional Property Investment Nigeria</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Investors don't need millions to get started. Fractional ownership allows people to buy shares in premium property projects, lowering entry barriers while still accessing lucrative real estate investments.
                                </p>
                            </div>

                            {/* Section 5 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Benefits of Investing Through Diasporawise</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Diasporawise was created specifically for diaspora investors who want secure, transparent, and profitable ways to invest in Nigerian real estate. Its benefits include:
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Secured and Compliant Investments</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Every project is vetted, regulated, and legally structured.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Transparency</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Frequent updates, project reports, and digital tracking.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Accessibility</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Start small with fractional ownership or scale up to full ownership.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Nation Building</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Drive job creation, infrastructure development, and home ownership while building personal wealth.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Cultural & Family Heritage</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Preserve wealth for children and grandchildren, while maintaining ties to home.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    For diaspora professionals looking to combine financial growth with meaningful impact, Diasporawise is the perfect fit.
                                </p>
                            </div>

                            {/* Section 6 */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Steps to Start Your Investment Journey</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Getting started with diaspora real estate investment in Nigeria through Diasporawise is straightforward:
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Join the Platform</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Create your account on Diasporawise.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Discover and Invest</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Explore residential, commercial, and fractional investment opportunities.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Pick a Project</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Choose the one that aligns with your financial goals.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Review Risks and Returns</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Read through the financials before investing.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Secure Your Payment</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Use trusted online or offline payment channels from anywhere in the world.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Track Your Investment</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Get regular updates, reports, and performance insights.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Grow Wealth Over Time</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Watch your investments appreciate while helping build Nigeria.
                                </p>
                            </div>

                            {/* Conclusion */}
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Conclusion: Building Wealth and Strengthening Nigeria</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    The Nigerian real estate market is more than just an investment—it's a pathway for diaspora Nigerians to create wealth, strengthen cultural ties, and contribute to national development.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Through residential property, commercial developments, land acquisition, or fractional investments, diaspora investors can secure strong financial returns while helping reduce Nigeria's housing deficit and infrastructure gap.
                                </p>
                                <p className="text-gray-700 leading-relaxed">
                                    With platforms like Diasporawise, risky informal property investments are now replaced with safe, transparent, and regulated opportunities.
                                </p>
                            </div>
                        </article>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Investment;
